// GPU time per rendered frame from EXT_disjoint_timer_query_webgl2 (Chrome and Edge on most desktops and many
// Android GPUs; not Safari, not Firefox by default). Queries are read back a few frames later without stalling.
// Without the extension, `available` is false and `ms` stays NaN.

const MAX_PENDING = 6;

export class GpuTimer {
  readonly available: boolean;
  private gl: WebGL2RenderingContext;
  private ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private pending: WebGLQuery[] = [];
  private free: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  private ring = new Float32Array(24);
  private n = 0;
  private i = 0;
  /** Mean GPU ms over the last frames that were measured (NaN until there are some). */
  ms = NaN;
  /** Time (performance.now) of the newest result. */
  at = 0;

  constructor(gl: WebGL2RenderingContext | WebGLRenderingContext) {
    this.gl = gl as WebGL2RenderingContext;
    const isGl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    this.ext = isGl2 ? (gl.getExtension('EXT_disjoint_timer_query_webgl2') as GpuTimer['ext']) : null;
    this.available = !!this.ext;
  }

  begin(): void {
    if (!this.ext || this.active || this.pending.length >= MAX_PENDING) return;
    const q = this.free.pop() ?? this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = q;
  }

  end(): void {
    if (!this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  /** Read finished queries (call once per frame, before begin). */
  poll(now: number): void {
    const gl = this.gl, ext = this.ext;
    if (!ext || !this.pending.length) return;
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
    while (this.pending.length) {
      const q = this.pending[0]!;
      if (!disjoint && !gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      this.pending.shift();
      if (!disjoint) {
        const ms = gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
        this.ring[this.i] = ms;
        this.i = (this.i + 1) % this.ring.length;
        if (this.n < this.ring.length) this.n++;
        this.at = now;
      }
      this.free.push(q);
    }
    if (this.n) {
      let s = 0;
      for (let k = 0; k < this.n; k++) s += this.ring[k]!;
      this.ms = s / this.n;
    }
  }

  /** Forget old samples (after a resolution change). */
  reset(): void {
    this.n = 0;
    this.i = 0;
    this.ms = NaN;
  }
}
